/**
 * Scrydex API Service
 * 
 * Service for fetching pricing data from the Scrydex API and storing it in Supabase.
 * This is a PAID API - use sparingly and cache results in the database.
 * 
 * Usage:
 * 1. Set VITE_SCRYDEX_API_KEY in your environment variables
 * 2. Use fetchAndStorePrices() to fetch and store prices for a card
 * 3. Use getCardPrices() to retrieve stored prices from the database
 */

import { supabase } from '../lib/supabase';
import {
  ScrydexRawPrice,
  ScrydexGradedPrice,
  CardPricingData,
  PriceHistoryPoint,
  RawCondition,
  GradingCompany,
  PriceTrends,
} from '../types/scrydex';

// =====================================================
// CONFIGURATION
// =====================================================

// Direct API URL (used by Edge Function)
const SCRYDEX_API_BASE_URL = 'https://api.scrydex.com/pokemon/v1';

// Supabase Edge Function URL for proxying requests (avoids CORS)
const getProxyUrl = (): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL environment variable is not set');
  }
  return `${supabaseUrl}/functions/v1/scrydex-proxy`;
};

// Check if we should use the proxy (browser) or direct API (server/local dev with CORS disabled)
const useProxy = (): boolean => {
  // Always use proxy in browser to avoid CORS
  return typeof window !== 'undefined';
};

// Get API key from environment
const getApiKey = (): string => {
  const apiKey = import.meta.env.VITE_SCRYDEX_API_KEY;
  if (!apiKey) {
    throw new Error('VITE_SCRYDEX_API_KEY environment variable is not set');
  }
  return apiKey;
};

// Get Team ID from environment
const getTeamId = (): string => {
  const teamId = import.meta.env.VITE_SCRYDEX_TEAM_ID;
  if (!teamId) {
    throw new Error('VITE_SCRYDEX_TEAM_ID environment variable is not set');
  }
  return teamId;
};

// =====================================================
// API FETCHING FUNCTIONS
// =====================================================

/**
 * Fetch card with prices from Scrydex API (same approach as the working script)
 * Uses the ?include=prices endpoint which returns prices embedded in variants
 * Uses Supabase Edge Function proxy to avoid CORS issues in browser
 * @param cardId - The Pokemon TCG API card ID (e.g., 'sv3-1')
 */
export async function fetchCardWithPrices(cardId: string): Promise<{
  rawPrices: ScrydexRawPrice[];
  gradedPrices: ScrydexGradedPrice[];
}> {
  try {
    let response: Response;
    
    if (useProxy()) {
      // Use Edge Function proxy to avoid CORS in browser
      // Credentials are stored securely in Edge Function secrets
      const proxyUrl = `${getProxyUrl()}?cardId=${encodeURIComponent(cardId)}&include=prices`;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      response = await fetch(proxyUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
      });
    } else {
      // Direct API call (server-side or CORS disabled)
      response = await fetch(`${SCRYDEX_API_BASE_URL}/cards/${cardId}?include=prices`, {
        headers: {
          'X-Api-Key': getApiKey(),
          'X-Team-ID': getTeamId(),
          'Content-Type': 'application/json',
        },
      });
    }

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`No card data found for ${cardId}`);
        return { rawPrices: [], gradedPrices: [] };
      }
      throw new Error(`Scrydex API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const cardData = data.data;
    
    if (!cardData || !cardData.variants) {
      console.log(`No variants/prices found for card ${cardId}`);
      return { rawPrices: [], gradedPrices: [] };
    }

    const rawPrices: ScrydexRawPrice[] = [];
    const gradedPrices: ScrydexGradedPrice[] = [];

    for (const variant of cardData.variants) {
      const finish = variant.name || 'normal';
      if (variant.prices) {
        for (const price of variant.prices) {
          if (price.type === 'raw') {
            rawPrices.push({
              condition: price.condition,
              finish,
              is_perfect: price.is_perfect || false,
              is_signed: price.is_signed || false,
              is_error: price.is_error || false,
              type: 'raw',
              low: price.low,
              market: price.market,
              currency: price.currency || 'USD',
              trends: price.trends,
            });
          } else if (price.type === 'graded') {
            gradedPrices.push({
              company: price.company,
              grade: price.grade,
              finish,
              is_perfect: price.is_perfect || false,
              is_signed: price.is_signed || false,
              is_error: price.is_error || false,
              type: 'graded',
              low: price.low,
              mid: price.mid,
              high: price.high,
              market: price.market,
              currency: price.currency || 'USD',
              trends: price.trends,
            });
          }
        }
      }
    }

    console.log(`Found ${rawPrices.length} raw and ${gradedPrices.length} graded prices for ${cardId}`);
    return { rawPrices, gradedPrices };
  } catch (error) {
    console.error(`Error fetching card with prices for ${cardId}:`, error);
    return { rawPrices: [], gradedPrices: [] };
  }
}

/**
 * @deprecated Use fetchCardWithPrices instead - this endpoint may not work for all cards
 * Fetch raw prices for a card from Scrydex API
 */
export async function fetchRawPricesFromApi(cardId: string): Promise<ScrydexRawPrice[]> {
  try {
    const response = await fetch(`${SCRYDEX_API_BASE_URL}/cards/${cardId}/prices/raw`, {
      headers: {
        'X-Api-Key': getApiKey(),
        'X-Team-ID': getTeamId(),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`No raw prices found for card ${cardId}`);
        return [];
      }
      throw new Error(`Scrydex API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.prices || [];
  } catch (error) {
    console.error(`Error fetching raw prices for ${cardId}:`, error);
    throw error;
  }
}

/**
 * @deprecated Use fetchCardWithPrices instead - this endpoint may not work for all cards
 * Fetch graded prices for a card from Scrydex API
 */
export async function fetchGradedPricesFromApi(cardId: string): Promise<ScrydexGradedPrice[]> {
  try {
    const response = await fetch(`${SCRYDEX_API_BASE_URL}/cards/${cardId}/prices/graded`, {
      headers: {
        'X-Api-Key': getApiKey(),
        'X-Team-ID': getTeamId(),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`No graded prices found for card ${cardId}`);
        return [];
      }
      throw new Error(`Scrydex API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.prices || [];
  } catch (error) {
    console.error(`Error fetching graded prices for ${cardId}:`, error);
    throw error;
  }
}

// =====================================================
// DATABASE STORAGE FUNCTIONS
// =====================================================

/**
 * Store raw prices in the database
 */
export async function storeRawPrices(cardId: string, prices: ScrydexRawPrice[]): Promise<void> {
  if (!prices.length) return;

  const seenKeys = new Set<string>();
  const uniquePrices = prices.filter((price) => {
    const key = `${price.condition}:${price.finish || 'normal'}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const records = uniquePrices.map((price) => ({
    card_id: cardId,
    condition: price.condition,
    finish: price.finish || 'normal',
    low_price: price.low,
    market_price: price.market,
    currency: price.currency || 'USD',
    trends: price.trends,
    fetched_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('card_prices_scrydex_raw')
    .upsert(records, {
      onConflict: 'card_id,condition,finish',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('Error storing raw prices:', error);
    throw error;
  }
}

// Valid grading companies in our database (same as script)
const VALID_GRADING_COMPANIES = ['PSA', 'CGC', 'BGS', 'TAG', 'SGC', 'OTHER'] as const;

function normalizeGradingCompany(company: string): GradingCompany {
  const upper = (company || '').toUpperCase();
  if (VALID_GRADING_COMPANIES.includes(upper as GradingCompany)) {
    return upper as GradingCompany;
  }
  // Map known variants
  if (['ACE', 'AGS', 'MNT', 'GMA'].includes(upper)) {
    return 'OTHER';
  }
  return 'OTHER';
}

/**
 * Store graded prices in the database
 */
export async function storeGradedPrices(cardId: string, prices: ScrydexGradedPrice[]): Promise<void> {
  if (!prices.length) return;

  const seenKeys = new Set<string>();
  const uniquePrices = prices.filter((price) => {
    const company = normalizeGradingCompany(price.company);
    const key = `${company}-${price.grade}-${price.is_perfect}-${price.is_signed}-${price.is_error}-${price.finish || 'normal'}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const records = uniquePrices.map((price) => ({
    card_id: cardId,
    grading_company: normalizeGradingCompany(price.company),
    grade: price.grade,
    finish: price.finish || 'normal',
    is_perfect: price.is_perfect || false,
    is_signed: price.is_signed || false,
    is_error: price.is_error || false,
    low_price: price.low,
    mid_price: price.mid,
    high_price: price.high,
    market_price: price.market,
    currency: price.currency || 'USD',
    trends: price.trends,
    fetched_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('card_prices_scrydex_graded')
    .upsert(records, {
      onConflict: 'card_id,grading_company,grade,is_perfect,is_signed,is_error,finish',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('Error storing graded prices:', error);
    throw error;
  }
}

/**
 * Store price history snapshots for a card
 * Generates historical data points from trends (same as the working script)
 */
export async function storePriceHistory(
  cardId: string,
  priceType: string,
  prices: { low?: number; market?: number; high?: number },
  trends?: PriceTrends | null
): Promise<void> {
  const today = new Date();
  const records: Array<{
    card_id: string;
    price_type: string;
    low_price: number | null;
    market_price: number | null;
    high_price: number | null;
    price_date: string;
  }> = [];

  // Always store today's price
  records.push({
    card_id: cardId,
    price_type: priceType,
    low_price: prices.low || null,
    market_price: prices.market || null,
    high_price: prices.high || null,
    price_date: today.toISOString().split('T')[0],
  });

  // Generate historical prices from trends data (same as the working script)
  if (trends && prices.market) {
    const currentPrice = prices.market;
    const trendPeriods = [
      { key: 'days_7' as const, days: 7 },
      { key: 'days_14' as const, days: 14 },
      { key: 'days_30' as const, days: 30 },
      { key: 'days_90' as const, days: 90 },
      { key: 'days_180' as const, days: 180 },
    ];

    for (const { key, days } of trendPeriods) {
      const trend = trends[key];
      if (trend && trend.price_change !== undefined) {
        // Calculate what the price was X days ago
        const historicalPrice = currentPrice - trend.price_change;
        if (historicalPrice > 0) {
          const pastDate = new Date(today);
          pastDate.setDate(pastDate.getDate() - days);
          
          records.push({
            card_id: cardId,
            price_type: priceType,
            low_price: null,
            market_price: historicalPrice,
            high_price: null,
            price_date: pastDate.toISOString().split('T')[0],
          });
        }
      }
    }
  }

  // Upsert all records
  const { error } = await supabase
    .from('card_price_history')
    .upsert(records, {
      onConflict: 'card_id,price_type,price_date',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('Error storing price history:', error);
    throw error;
  }
}

// =====================================================
// MAIN FETCH AND STORE FUNCTION
// =====================================================

/**
 * Fetch all prices from Scrydex API and store them in the database
 * This is the main function to use for populating pricing data.
 * 
 * Uses the /cards/{id}?include=prices endpoint (same as the working script)
 * which embeds prices in the card variants response.
 * 
 * @param cardId - The Pokemon TCG API card ID
 * @returns Object with raw and graded price counts
 */
export async function fetchAndStorePrices(cardId: string): Promise<{
  rawCount: number;
  gradedCount: number;
}> {
  console.log(`Fetching Scrydex prices for card: ${cardId}`);

  // Use the working endpoint that embeds prices in variants
  const { rawPrices, gradedPrices } = await fetchCardWithPrices(cardId);

  // Store prices in database
  await Promise.all([
    storeRawPrices(cardId, rawPrices),
    storeGradedPrices(cardId, gradedPrices),
  ]);

  // Store price history for all raw conditions per finish
  const conditions = ['NM', 'LP', 'MP', 'HP', 'DM'] as const;
  const finishes = [...new Set(rawPrices.map(p => p.finish || 'normal'))];
  for (const finish of finishes) {
    for (const condition of conditions) {
      const price = rawPrices.find((p) => p.condition === condition && (p.finish || 'normal') === finish);
      if (price && price.market) {
        const historyKey = finish === 'normal' ? `raw_${condition}` : `raw_${condition}_${finish}`;
        await storePriceHistory(cardId, historyKey, {
          low: price.low,
          market: price.market,
        }, price.trends);
      }
    }
  }

  // Store price history for PSA 10 (most commonly referenced graded)
  const psa10Price = gradedPrices.find((p) => p.company === 'PSA' && p.grade === '10' && !p.is_perfect);
  if (psa10Price) {
    await storePriceHistory(cardId, 'graded_PSA_10', {
      low: psa10Price.low,
      market: psa10Price.market,
      high: psa10Price.high,
    }, psa10Price.trends);
  }

  console.log(`Stored ${rawPrices.length} raw prices and ${gradedPrices.length} graded prices for ${cardId}`);

  return {
    rawCount: rawPrices.length,
    gradedCount: gradedPrices.length,
  };
}

// =====================================================
// DATABASE RETRIEVAL FUNCTIONS
// =====================================================

/**
 * Get all stored prices for a card from the database
 * This is what the frontend should use to display prices.
 */
export async function getCardPrices(cardId: string): Promise<CardPricingData | null> {
  try {
    // Fetch raw prices
    const { data: rawData, error: rawError } = await supabase
      .from('card_prices_scrydex_raw')
      .select('*')
      .eq('card_id', cardId)
      .order('condition');

    if (rawError) {
      console.error('Error fetching raw prices:', rawError);
    }

    // Fetch graded prices
    const { data: gradedData, error: gradedError } = await supabase
      .from('card_prices_scrydex_graded')
      .select('*')
      .eq('card_id', cardId)
      .order('grading_company')
      .order('grade', { ascending: false });

    if (gradedError) {
      console.error('Error fetching graded prices:', gradedError);
    }

    // If no data found, return null
    if ((!rawData || rawData.length === 0) && (!gradedData || gradedData.length === 0)) {
      return null;
    }

    return {
      cardId,
      raw: (rawData || []).map((r) => ({
        condition: r.condition as RawCondition,
        finish: r.finish || 'normal',
        low: r.low_price,
        market: r.market_price,
        trends: r.trends as PriceTrends | null,
        updatedAt: r.updated_at,
      })),
      graded: (gradedData || []).map((g) => ({
        company: g.grading_company as GradingCompany,
        grade: g.grade,
        finish: g.finish || 'normal',
        isPerfect: g.is_perfect,
        isSigned: g.is_signed,
        isError: g.is_error,
        low: g.low_price,
        mid: g.mid_price,
        high: g.high_price,
        market: g.market_price,
        trends: g.trends as PriceTrends | null,
        updatedAt: g.updated_at,
      })),
    };
  } catch (error) {
    console.error('Error getting card prices:', error);
    return null;
  }
}

/**
 * Get price history for a card (for charting)
 */
export async function getCardPriceHistory(
  cardId: string,
  priceType: string = 'raw_NM',
  days: number = 180
): Promise<PriceHistoryPoint[]> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('card_price_history')
      .select('price_date, low_price, market_price, high_price')
      .eq('card_id', cardId)
      .eq('price_type', priceType)
      .gte('price_date', startDate.toISOString().split('T')[0])
      .order('price_date', { ascending: true });

    if (error) {
      console.error('Error fetching price history:', error);
      return [];
    }

    return (data || []).map((point) => ({
      date: point.price_date,
      low: point.low_price,
      market: point.market_price,
      high: point.high_price,
    }));
  } catch (error) {
    console.error('Error getting price history:', error);
    return [];
  }
}

/**
 * Check if a card has Scrydex pricing data
 */
export async function hasScrdyexPrices(cardId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('card_prices_scrydex_raw')
    .select('id')
    .eq('card_id', cardId)
    .limit(1);

  if (error) {
    console.error('Error checking for Scrydex prices:', error);
    return false;
  }

  return data !== null && data.length > 0;
}

/**
 * Get last fetch time for a card
 */
export async function getLastFetchTime(cardId: string): Promise<Date | null> {
  const { data, error } = await supabase
    .from('card_prices_scrydex_raw')
    .select('fetched_at')
    .eq('card_id', cardId)
    .order('fetched_at', { ascending: false })
    .limit(1);

  if (error || !data || data.length === 0) {
    return null;
  }

  return new Date(data[0].fetched_at);
}

